import { ClockCircleOutlined, TeamOutlined } from "@ant-design/icons";
import {
    formatDateTimeAndTimezone,
    formatDateWithTimezone,
    formatTimeAndTimezone,
} from "@impero-com/common/utils/formatting";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    ActivityTimeline,
    ActivityTimelineComment,
    ActivityTimelineDivider,
    ActivityTimelineItem,
    Alert,
    CalendarDueIcon,
    Divider,
    SummaryCard,
    SummaryCardColumn,
    SummaryCardIconItem,
    SummaryCardItem,
    Svg,
    renderMarkdown,
    useBelowBreakpoint,
} from "@impero-com/ui";
import { rawPalette } from "@impero-com/ui/styles/theme.css";
import { Typography } from "antd";
import Title from "antd/es/typography/Title";
import moment from "moment-timezone";

import {
    IanaTimezoneView,
    ActorDto,
    PeopleInvolvedDto,
    TimelineEntryDto,
    TimelineEventTypeDto,
    WaitedForDto,
    WaitedRoleDto,
} from "api/generated/model";
import { eventSourceTag } from "components/eventSourceTag/eventSourceTag";
import GroupIconSvg from "img/group-icon.svg";

import * as theme from "./ControlActivityTimeline.css";

const { Text } = Typography;

// Props mirror the timeline endpoint's `ActivityTimelineDto`, plus the viewing
// user's timezone which every date is rendered in.
interface ControlActivityTimelineProps {
    dueDate: string;
    peopleInvolved: PeopleInvolvedDto;
    rows: TimelineEntryDto[];
    completedAt: string | null;
    overdue: boolean;
    timezone: IanaTimezoneView;
}

export function ControlActivityTimeline({
    dueDate,
    peopleInvolved,
    rows,
    completedAt,
    overdue,
    timezone,
}: ControlActivityTimelineProps) {
    const { t } = useTranslation();

    const events = rows.filter(
        (row): row is Extract<TimelineEntryDto, { type: "event" }> =>
            row.type === "event"
    );

    return (
        <div className={theme.root}>
            <TimelineSummary
                events={events}
                completedAt={completedAt}
                overdue={overdue}
                dueDate={dueDate}
                peopleInvolved={peopleInvolved}
                timezone={timezone}
            />

            <Title level={5}>{t("ControlActivityTimeline_Title")}</Title>
            {rows.length === 0 ? (
                <Text type="secondary">
                    {t("ControlActivityTimeline_Empty")}
                </Text>
            ) : (
                <ActivityTimeline>
                    {rows.map((row, index) => (
                        <TimelineRow
                            key={index}
                            row={row}
                            timezone={timezone}
                        />
                    ))}
                </ActivityTimeline>
            )}
        </div>
    );
}

interface TimelineSummaryProps {
    events: Array<Extract<TimelineEntryDto, { type: "event" }>>;
    completedAt: string | null;
    overdue: boolean;
    dueDate: string;
    peopleInvolved: PeopleInvolvedDto;
    timezone: IanaTimezoneView;
}

function TimelineSummary({
    events,
    completedAt,
    overdue,
    dueDate,
    peopleInvolved,
    timezone,
}: TimelineSummaryProps) {
    const { t, i18n } = useTranslation();
    // Below `sm` the card is too narrow for side-by-side columns, so stack them
    // and flip the divider to horizontal.
    const isStacked = useBelowBreakpoint("sm");
    const none = t("ControlActivityTimeline_None");
    const responsible = peopleInvolved.responsible;
    const completed = completedAt !== null;

    const duration = activityDuration(events, completedAt, i18n.language);

    const heading = completed
        ? t("ControlActivityTimeline_SummaryCompleted", {
              duration,
              count: events.length,
          })
        : t("ControlActivityTimeline_SummaryOpen", {
              duration,
              count: events.length,
          });

    return (
        <SummaryCard vertical={isStacked}>
            <SummaryCardColumn heading={heading}>
                <SummaryCardIconItem
                    icon={<CalendarDueIcon size={22} />}
                    label={t("ControlActivityTimeline_SummaryActivityDueDate")}
                    value={formatDateTimeAndTimezone(dueDate, timezone)}
                    tone={overdue ? "danger" : "default"}
                />
            </SummaryCardColumn>

            <Divider orientation={isStacked ? "horizontal" : "vertical"} />

            <SummaryCardColumn
                heading={t("ControlActivityTimeline_SummaryPeopleInvolved")}
            >
                <SummaryCardItem
                    label={t("ControlActivityTimeline_SummaryResponsible")}
                    value={
                        responsible ? <ActorLabel actor={responsible} /> : none
                    }
                />
                {(peopleInvolved.firstReviewer ||
                    peopleInvolved.secondReviewer) && (
                    <SummaryCardItem
                        label={t(
                            "ControlActivityTimeline_SummaryFirstReviewer"
                        )}
                        value={
                            peopleInvolved.firstReviewer ? (
                                <ActorLabel
                                    actor={peopleInvolved.firstReviewer}
                                />
                            ) : (
                                none
                            )
                        }
                    />
                )}
                {peopleInvolved.secondReviewer && (
                    <SummaryCardItem
                        label={t(
                            "ControlActivityTimeline_SummarySecondReviewer"
                        )}
                        value={
                            <ActorLabel actor={peopleInvolved.secondReviewer} />
                        }
                    />
                )}
            </SummaryCardColumn>
        </SummaryCard>
    );
}

interface TimelineRowProps {
    row: TimelineEntryDto;
    timezone: IanaTimezoneView;
}

function TimelineRow({ row, timezone }: TimelineRowProps) {
    const { t } = useTranslation();
    if (row.type === "dueDate") {
        return (
            <ActivityTimelineDivider
                lineStyle={row.isCurrent ? "solid" : "dashed"}
                color={
                    row.isOverdue
                        ? rawPalette.base.red[500]
                        : rawPalette.base.blueGrey[300]
                }
            >
                {t(
                    row.isCurrent
                        ? "ControlActivityTimeline_ActivityDueDate"
                        : "ControlActivityTimeline_PreviousActivityDueDate",
                    { date: formatDateTimeAndTimezone(row.date, timezone) }
                )}
            </ActivityTimelineDivider>
        );
    }

    // Every known event type is mapped; an unmapped one is a backend/data issue,
    // so show it verbatim rather than dressing it up as a real label.
    const meta = EVENT_META[row.eventType];
    const label = meta ? t(meta.labelKey) : row.eventType;

    const isAssignment = ASSIGNMENT_EVENTS.has(row.eventType);
    const targetName = row.targetName?.trim();
    const assignedTo =
        isAssignment && targetName ? (
            <>
                {t("ControlActivityTimeline_AssignedTo", {
                    name: targetName,
                })}
                {row.targetIsGroup && <GroupIcon />}
            </>
        ) : undefined;
    // Assignment events may have a legacy System kind without known System origin.
    const assignmentActorName =
        row.source === "System"
            ? t("ControlActivityTimeline_ActorSystem")
            : row.actorName?.trim();
    const assignmentActor = assignmentActorName
        ? t("ControlActivityTimeline_By", { name: assignmentActorName })
        : undefined;
    const actor = isAssignment ? (
        assignmentActor
    ) : (
        <>
            {t("ControlActivityTimeline_By", {
                name: eventActorLabel(t, row),
            })}
            {row.actorIsGroup && <GroupIcon />}
        </>
    );
    const source = eventSourceTag(t, row.source);
    const subtitle =
        actor || assignedTo || source ? (
            <>
                {actor}
                {actor && assignedTo && " — "}
                {assignedTo}
                {source}
            </>
        ) : undefined;

    return (
        <ActivityTimelineItem
            date={formatDateWithTimezone(row.date, timezone)}
            time={formatTimeAndTimezone(row.date, timezone)}
            title={label}
            subtitle={subtitle}
            color={meta?.color}
        >
            {row.dueDateChange && (
                <>
                    <span className={theme.dueDateChange}>
                        {t("ControlActivityTimeline_PreviousActivityDueDate", {
                            date: formatDateTimeAndTimezone(
                                row.dueDateChange.previousDueDate,
                                timezone
                            ),
                        })}
                    </span>
                    <span className={theme.dueDateChange}>
                        {t("ControlActivityTimeline_NewActivityDueDate", {
                            date: formatDateTimeAndTimezone(
                                row.dueDateChange.newDueDate,
                                timezone
                            ),
                        })}
                    </span>
                </>
            )}
            {row.message && (
                <ActivityTimelineComment>
                    {renderMarkdown(row.message)}
                </ActivityTimelineComment>
            )}
            {row.waitedFor && (
                <GapLabel waitedFor={row.waitedFor} timezone={timezone} />
            )}
        </ActivityTimelineItem>
    );
}

interface GapLabelProps {
    waitedFor: WaitedForDto;
    timezone: IanaTimezoneView;
}

function GapLabel({ waitedFor, timezone }: GapLabelProps) {
    const { t } = useTranslation();
    const who = waitedFor.actor
        ? `${stepLabel(t, waitedFor.role)} ${actorName(waitedFor.actor)}`
        : stepLabel(t, waitedFor.role);
    const date = waitedFor.dueDate
        ? formatDateTimeAndTimezone(waitedFor.dueDate, timezone)
        : t("ControlActivityTimeline_None");

    return (
        <Alert
            className={theme.gapLabel}
            // Neutral while on time; warning once the step is overdue.
            type={waitedFor.isOverdue ? "warning" : "info"}
            showIcon
            icon={<ClockCircleOutlined />}
            message={t("ControlActivityTimeline_WaitingOn", { who, date })}
        />
    );
}

// Muted user-group chip shown after an actor name: a people icon + group name.
function ActorGroup({ team }: { team: string }) {
    return (
        <span className={theme.group}>
            <TeamOutlined />
            {team}
        </span>
    );
}

function ActorLabel({ actor }: { actor: ActorDto }) {
    return (
        <>
            {actor.name}
            {actor.team && <ActorGroup team={actor.team} />}
            {actor.isGroup && <GroupIcon />}
        </>
    );
}

// Muted group icon shown after the name of a group actor.
function GroupIcon() {
    return (
        <span className={theme.groupIcon}>
            <Svg src={GroupIconSvg} />
        </span>
    );
}

// Event types that display the performer and assignment target separately.
const ASSIGNMENT_EVENTS = new Set<TimelineEventTypeDto>([
    "created",
    "responsibleReassigned",
    "reviewerReassigned",
    "firstReviewerReassigned",
    "secondReviewerReassigned",
]);

// Translation keys for event chip labels — all interpolation-free, so `t` takes
// no options.
type EventLabelKey =
    | "ControlActivityTimeline_Event_Created"
    | "ControlActivityTimeline_Event_Reminded"
    | "ControlActivityTimeline_Event_SubmittedForReview"
    | "ControlActivityTimeline_Event_ApprovedByFirstReviewer"
    | "ControlActivityTimeline_Event_RejectedAndReassignedToResponsible"
    | "ControlActivityTimeline_Event_RejectedAndReassignedToFirstReviewer"
    | "ControlActivityTimeline_Event_Completed"
    | "ControlActivityTimeline_Event_CompletedAndReviewed"
    | "ControlActivityTimeline_Event_AutoCompleted"
    | "ControlActivityTimeline_Event_Reopened"
    | "ControlActivityTimeline_Event_ReopenedAndDueDateExtended"
    | "ControlActivityTimeline_Event_ResponsibleReassigned"
    | "ControlActivityTimeline_Event_ReviewerReassigned"
    | "ControlActivityTimeline_Event_FirstReviewerReassigned"
    | "ControlActivityTimeline_Event_SecondReviewerReassigned"
    | "ControlActivityTimeline_Event_Archived"
    | "ControlActivityTimeline_Event_Unarchived"
    | "ControlActivityTimeline_Event_Claimed"
    | "ControlActivityTimeline_Event_Unclaimed"
    | "ControlActivityTimeline_Event_DueDateExtended"
    | "ControlActivityTimeline_Event_Seen"
    | "ControlActivityTimeline_Event_System"
    | "ControlActivityTimeline_Event_Unknown";

// Maps a backend event type to its chip label and dot colour.
const EVENT_META: Record<
    TimelineEventTypeDto,
    { labelKey: EventLabelKey; color?: string }
> = {
    created: { labelKey: "ControlActivityTimeline_Event_Created" },
    reminded: {
        labelKey: "ControlActivityTimeline_Event_Reminded",
        color: rawPalette.base.orange[500],
    },
    autoReminded: {
        labelKey: "ControlActivityTimeline_Event_Reminded",
        color: rawPalette.base.orange[500],
    },
    pendingFirstReview: {
        labelKey: "ControlActivityTimeline_Event_SubmittedForReview",
    },
    pendingSecondReview: {
        labelKey: "ControlActivityTimeline_Event_ApprovedByFirstReviewer",
    },
    completedReviewIrrelevant: {
        labelKey: "ControlActivityTimeline_Event_Completed",
        color: rawPalette.base.green[500],
    },
    completedAndReviewed: {
        labelKey: "ControlActivityTimeline_Event_CompletedAndReviewed",
        color: rawPalette.base.green[500],
    },
    autoCompleted: {
        labelKey: "ControlActivityTimeline_Event_AutoCompleted",
        color: rawPalette.base.green[500],
    },
    rejectedByFirstReviewerAndAssignedToResponsible: {
        labelKey:
            "ControlActivityTimeline_Event_RejectedAndReassignedToResponsible",
        color: rawPalette.base.red[500],
    },
    rejectedBySecondReviewerAndAssignedToResponsible: {
        labelKey:
            "ControlActivityTimeline_Event_RejectedAndReassignedToResponsible",
        color: rawPalette.base.red[500],
    },
    rejectedBySecondReviewerAndAssignedToFirstReviewer: {
        labelKey:
            "ControlActivityTimeline_Event_RejectedAndReassignedToFirstReviewer",
        color: rawPalette.base.red[500],
    },
    reopened: {
        labelKey: "ControlActivityTimeline_Event_Reopened",
        color: rawPalette.base.red[500],
    },
    reopenedAndDueDateExtended: {
        labelKey: "ControlActivityTimeline_Event_ReopenedAndDueDateExtended",
        color: rawPalette.base.red[500],
    },
    responsibleReassigned: {
        labelKey: "ControlActivityTimeline_Event_ResponsibleReassigned",
    },
    reviewerReassigned: {
        labelKey: "ControlActivityTimeline_Event_ReviewerReassigned",
    },
    firstReviewerReassigned: {
        labelKey: "ControlActivityTimeline_Event_FirstReviewerReassigned",
    },
    secondReviewerReassigned: {
        labelKey: "ControlActivityTimeline_Event_SecondReviewerReassigned",
    },
    archived: { labelKey: "ControlActivityTimeline_Event_Archived" },
    unarchived: { labelKey: "ControlActivityTimeline_Event_Unarchived" },
    claimed: { labelKey: "ControlActivityTimeline_Event_Claimed" },
    unclaimed: { labelKey: "ControlActivityTimeline_Event_Unclaimed" },
    dueDateExtended: {
        labelKey: "ControlActivityTimeline_Event_DueDateExtended",
    },
    seen: { labelKey: "ControlActivityTimeline_Event_Seen" },
    system: { labelKey: "ControlActivityTimeline_Event_System" },
    unknown: { labelKey: "ControlActivityTimeline_Event_Unknown" },
};

// "<name> (<team>)" when the actor belongs to a user group, otherwise the name.
function actorName(actor: ActorDto | null | undefined): string {
    if (!actor) {
        return "";
    }
    return actor.team ? `${actor.name} (${actor.team})` : actor.name;
}

function eventActorLabel(
    t: TFunction,
    event: Extract<TimelineEntryDto, { type: "event" }>
): string {
    switch (event.actorKind) {
        case "system":
            return t("ControlActivityTimeline_ActorSystem");
        case "user":
            return event.actorName ?? "";
        default:
            return assertNever(event.actorKind);
    }
}

function stepLabel(t: TFunction, step: WaitedRoleDto): string {
    switch (step) {
        case "responsible":
            return t("ControlActivityTimeline_StepResponsible");
        case "firstReviewer":
            return t("ControlActivityTimeline_StepFirstReviewer");
        case "secondReviewer":
            return t("ControlActivityTimeline_StepSecondReviewer");
        default:
            return assertNever(step);
    }
}

// Span shown in the summary heading. A completed activity is measured to its
// completion (Created -> completion = cycle time); an open one is still running,
// so measure to now. moment picks the coarsest unit ("a few seconds", "2 hours",
// "3 days") and localizes via the app locale, not its browser-following default.
function activityDuration(
    events: Array<Extract<TimelineEntryDto, { type: "event" }>>,
    completedAt: string | null,
    locale: string
): string {
    if (events.length === 0) {
        return "";
    }
    const start = moment(events[0].date);
    const end = completedAt ? moment(completedAt) : moment();
    return moment.duration(end.diff(start)).locale(locale).humanize();
}
